import Vue from "vue";
import {Constructor} from "../../global/constructor";
import ObjectModelRelation from "../models/objectModelRelations/objectModelRelation";
import {ArrayHelper} from "../helpers/arrayHelper";
import ObjectModelBuilderModel from "../models/tracked/objectModelBuilderModel";
import {ModelHelper} from "../helpers/modelHelper";
import {ObjectModelRelationType} from "../enums/objectModelRelationType";
import ObjectModelRelationContainerBase from "~/mixins/objectModelRelationContainerBase";

export default function ObjectModelRelationContainer<TBase extends Constructor<ObjectModelBuilderModel>>(Base: TBase) {
  return class ObjectModelContainer extends ObjectModelRelationContainerBase(Base) {
    updateRelationPositions(movedRelation: ObjectModelRelation, position: number) {
      ArrayHelper.move(this.sorted_relation_client_ids, movedRelation.client_id, position);
      this.updateRelationPositionsForParent(this, movedRelation, position);
    }

    updateRelationPositionsForParent(parent: ObjectModelContainer, movedRelation: ObjectModelRelation, position: number) {
      this.updateSortedRelationClientIds(parent, ArrayHelper.move(parent.sorted_relation_client_ids, movedRelation.client_id, position));
    }

    moveRelationToParent(relationId: string, parentId: string, newPosition: number): void {
      const relation: ObjectModelRelation = this.findObjectModelRelation(relationId);
      const newParent: ObjectModelContainer = this.findObjectModelRelationContainer(parentId);
      const oldParent = relation.parent;

      if (newParent === oldParent) {
        this.updateRelationPositionsForParent(oldParent, relation, newPosition);
      } else {
        oldParent.trackCreationDeletion(relation.client_id, relation.asJson(), null, relation);
        ArrayHelper.remove(oldParent.sorted_relation_client_ids, relation.client_id);
        newParent.trackCreationDeletion(relation.client_id, null, relation.asJson(), relation);
      }
      newParent.updateRelationPositionsForParent(newParent, relation, newPosition);

      if (newParent !== oldParent) {
        Vue.delete(oldParent[relation.rootName], relation.client_id);
        relation.parent = newParent;
        Vue.set(newParent[relation.rootName], relation.client_id, relation);
      }

      this.saveChangesToStore({area: null, modelId: parentId});
    }

    addObjectModelRelation(parent: ObjectModelContainer, relation: ObjectModelRelation, position: number = parent.sorted_relation_client_ids.length) {
      this.updateSortedRelationClientIds(parent, ArrayHelper.add(parent.sorted_relation_client_ids.slice(), relation.client_id, position));
    }

    removeObjectModelRelation(relationOrClientId: string | ObjectModelRelation) {
      const clientId = ModelHelper.getClientId(relationOrClientId);
      this.updateSortedRelationClientIds(this, ArrayHelper.remove(this.sorted_relation_client_ids.slice(), clientId));
    }

    reconcileSortedRelationClientIds(): void {
      if (!this.sorted_relation_client_ids) {
        return;
      }
      const clientIds = Object.keys(this.object_model_relations)
      // remove ids that should not be present
      for (let clientId of this.sorted_relation_client_ids) {
        if (clientIds.includes(clientId)) {
          continue;
        }
        this.removeObjectModelRelation(clientId);
      }
      // add ids that should be present
      for (let clientId of clientIds) {
        if (this.sorted_relation_client_ids.includes(clientId)) {
          continue;
        }
        this.addObjectModelRelation(this, this.object_model_relations[clientId]);
      }
    }

    objectModelRelationsOfType(...types: ObjectModelRelationType[]): ObjectModelRelation[] {
      return this.objectModelRelationsArray.filter(r => types.includes(r.relation_type));
    }

    updateSortedRelationClientIds(parent: ObjectModelContainer, newClientIds: string[]): void {
      if (!ArrayHelper.equals(parent.sorted_relation_client_ids, newClientIds)) {
        parent.sorted_relation_client_ids = Object.assign([], newClientIds);
      }
    }
  };
}